// store/studyYears/slice.ts

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { StudyYearsState } from '@/types/study-years';
import {
  getStudyYears,
  getStudyYearById,
  updateStudyYear,
  publishStudyYear,
  getStudyYearPublications,
  getStudyYearChanges,
  downloadStudyYearExcel,
  getStudyYearSegments,
  addStudyYearSegment,
  getStudyYearCategories,
  deleteStudyYearSegment,
  getStudyYearCompanies,
  addStudyYearCompany,
  deleteStudyYearCompany,
  getStudyYearForecasts,
  updateStudyYearForecasts,
} from './thunks';

const initialState: StudyYearsState = {
  // Core data
  studyId: '',
  studyName: '',
  years: [],
  yearSelected: null,
  categorySelected: null,
  companySelected: null,
  yearProfile: null,

  // Related data
  publications: [],
  changes: [],
  categories: [],
  segments: [], // TODO: deprecate this and move to categories with tree structure.
  companies: [],
  forecasts: [],
  briefings: [],

  // Loading states
  isLoading: false,
  isPublishing: false,
  isDownloading: false,
  isUpdatingForecasts: false,

  // Error states
  error: null,
  publishError: null,
  downloadError: null,
  forecastsError: null,

  // Success flags
  publishSuccess: false,
  downloadSuccess: false,
  forecastsUpdateSuccess: false,
};

export const studyYearsSlice = createSlice({
  name: 'studyYears',
  initialState,
  reducers: {
    // ------------------------------------ CLEAR ACTIONS ------------------------------------

    clearError: state => {
      state.error = null;
    },

    clearPublishError: state => {
      state.publishError = null;
    },

    clearDownloadError: state => {
      state.downloadError = null;
    },

    clearForecastsError: state => {
      state.forecastsError = null;
    },

    clearAllErrors: state => {
      state.error = null;
      state.publishError = null;
      state.downloadError = null;
      state.forecastsError = null;
    },

    // ----------------------------------- SUCCESS FLAGS -----------------------------------

    clearPublishSuccess: state => {
      state.publishSuccess = false;
    },

    clearDownloadSuccess: state => {
      state.downloadSuccess = false;
    },

    clearForecastsUpdateSuccess: state => {
      state.forecastsUpdateSuccess = false;
    },

    clearAllSuccessFlags: state => {
      state.publishSuccess = false;
      state.downloadSuccess = false;
      state.forecastsUpdateSuccess = false;
    },

    // ------------------------------------ RESET ACTIONS ------------------------------------

    resetCurrentStudyYear: state => {
      state.yearSelected = null;
    },

    resetRelatedData: state => {
      state.publications = [];
      state.changes = [];
      state.segments = [];
      state.categories = [];
      state.companies = [];
      state.forecasts = [];
    },

    resetState: () => initialState,
  },
  extraReducers: builder => {
    builder
      // --------------------------------------- CORE STUDY YEARS ---------------------------------------

      // Get Study Years
      .addCase(getStudyYears.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getStudyYears.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.years = action.payload;
      })
      .addCase(getStudyYears.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Get Study Year By ID
      .addCase(getStudyYearById.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getStudyYearById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.yearSelected = action.payload;
      })
      .addCase(getStudyYearById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Update Study Year
      .addCase(updateStudyYear.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateStudyYear.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(updateStudyYear.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // --------------------------------- STUDY YEAR MANAGEMENT ---------------------------------

      // Publish Study Year
      .addCase(publishStudyYear.pending, state => {
        state.isPublishing = true;
        state.publishError = null;
        state.publishSuccess = false;
      })
      .addCase(publishStudyYear.fulfilled, (state, action) => {
        state.isPublishing = false;
        state.publishError = null;
        state.publishSuccess = true;
      })
      .addCase(publishStudyYear.rejected, (state, action) => {
        state.isPublishing = false;
        state.publishError = action.payload as string;
        state.publishSuccess = false;
      })

      // Get Study Year Publications
      .addCase(getStudyYearPublications.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getStudyYearPublications.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.publications = action.payload;
      })
      .addCase(getStudyYearPublications.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Get Study Year Changes
      .addCase(getStudyYearChanges.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getStudyYearChanges.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.changes = action.payload;
      })
      .addCase(getStudyYearChanges.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Download Study Year Excel
      .addCase(downloadStudyYearExcel.pending, state => {
        state.isDownloading = true;
        state.downloadError = null;
        state.downloadSuccess = false;
      })
      .addCase(downloadStudyYearExcel.fulfilled, state => {
        state.isDownloading = false;
        state.downloadError = null;
        state.downloadSuccess = true;
      })
      .addCase(downloadStudyYearExcel.rejected, (state, action) => {
        state.isDownloading = false;
        state.downloadError = action.payload as string;
        state.downloadSuccess = false;
      })

      // ----------------------------------- SEGMENTS MANAGEMENT -----------------------------------

      // Get Study Year Segments
      .addCase(getStudyYearSegments.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getStudyYearSegments.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.segments = action.payload;
      })
      .addCase(getStudyYearSegments.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Add Study Year Segment
      .addCase(addStudyYearSegment.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addStudyYearSegment.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.segments.push(action.payload);
      })
      .addCase(addStudyYearSegment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Get Study Year Categories
      .addCase(getStudyYearCategories.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getStudyYearCategories.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.categories = action.payload;
      })
      .addCase(getStudyYearCategories.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Delete Study Year Segment
      .addCase(deleteStudyYearSegment.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteStudyYearSegment.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        // Remove from segments array
        state.segments = state.segments.filter(
          segment => segment.study_year_category_id !== action.payload.studyYearCategoryId
        );
      })
      .addCase(deleteStudyYearSegment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // ---------------------------------- COMPANIES MANAGEMENT ----------------------------------

      // Get Study Year Companies
      .addCase(getStudyYearCompanies.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getStudyYearCompanies.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.companies = action.payload;
      })
      .addCase(getStudyYearCompanies.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Add Study Year Company
      .addCase(addStudyYearCompany.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addStudyYearCompany.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        // Note: API returns 201 with empty body, so we might need to refetch
      })
      .addCase(addStudyYearCompany.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Delete Study Year Company
      .addCase(deleteStudyYearCompany.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteStudyYearCompany.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        // Remove from companies array
        state.companies = state.companies.filter(
          company => company.study_year_company_id !== action.payload.studyYearCompanyId
        );
      })
      .addCase(deleteStudyYearCompany.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // ---------------------------------- FORECASTS MANAGEMENT ----------------------------------

      // Get Study Year Forecasts
      .addCase(getStudyYearForecasts.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getStudyYearForecasts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.forecasts = action.payload;
      })
      .addCase(getStudyYearForecasts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Update Study Year Forecasts
      .addCase(updateStudyYearForecasts.pending, state => {
        state.isUpdatingForecasts = true;
        state.forecastsError = null;
        state.forecastsUpdateSuccess = false;
      })
      .addCase(updateStudyYearForecasts.fulfilled, (state, action) => {
        state.isUpdatingForecasts = false;
        state.forecastsError = null;
        state.forecastsUpdateSuccess = true;
      })
      .addCase(updateStudyYearForecasts.rejected, (state, action) => {
        state.isUpdatingForecasts = false;
        state.forecastsError = action.payload as string;
        state.forecastsUpdateSuccess = false;
      });
  },
});

export const {
  clearError,
  clearPublishError,
  clearDownloadError,
  clearForecastsError,
  clearAllErrors,
  clearPublishSuccess,
  clearDownloadSuccess,
  clearForecastsUpdateSuccess,
  clearAllSuccessFlags,
  resetCurrentStudyYear,
  resetRelatedData,
  resetState,
} = studyYearsSlice.actions;

export const studyYearsReducer = studyYearsSlice.reducer;
